import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { AlertOctagon, Check, Info, Plus, X } from "react-feather";
import "../calendar.css";
import {
  getSelfPermissions,
  PermissionRecord,
  postPermission,
} from "../../../../core/api/PermissionReq";
import { useAuthToken } from "../../../../core/data/redux/useAuthToken";
import { toast } from "react-toastify";
import { Dropdown, Menu, MenuProps, Button, TimePicker, Input } from "antd";
import { DownOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import useGetEmployees from "../../../../core/api/useGetEmployees";
import { API_BASE_PATH } from "../../../../environment";
import duration from "dayjs/plugin/duration";
import {
  getSelfOnDuties,
  OnDutyRecord,
  OnDutyRequest,
  postOnDuty,
} from "../../../../core/api/OnDuty";
import {
  getSelfWorkFromHome,
  postWorkFromHome,
  WorkFromHomeRecord,
  WorkFromHomeRequest,
} from "../../../../core/api/useWFH";
// Import the new APIs
import {
  postApplyLeave,
  getSelfApplyLeave,
  ApplyLeaveRecord,
  ApplyLeaveCreate,
  putUpdateLeave,
} from "../../../../core/api/applyLeave";
import {
  getAllLeaveTypes,
  getLeaveBalanceByEmployee,
  LeaveTypeRecord,
  LeaveBalanceRecord,
} from "../../../../core/api/leaveType";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import { RootState } from "../../../../core/data/redux/store";
import { Form } from "react-bootstrap";
import { Modal } from "bootstrap";
import { fetchEmployeeImage } from "../../../../core/api/fetchEmployeeImage";

dayjs.extend(duration);

const LeaveEmployee = () => {
  const BASE_PATH = API_BASE_PATH;
  const empToken = useAuthToken();
  const [empImages, setEmpImages] = useState<Record<string, string>>({});
  const empId = useSelector((state: RootState) => state.auth.employeeId);
  // Decode token to get current user's employee_id
  const decodedToken = empToken ? jwtDecode<{ id: string }>(empToken) : null;
  const currentEmployeeId = decodedToken?.id || "";
  const today = new Date().toISOString().split("T")[0];
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");

  // Permission states
  const [permissionData, setPermissionData] = useState({
    date_apply: "",
    start_time: "",
    end_time: "",
    duration_minutes: 0,
    reason: "",
    notify: "",
  });
  const [permissionCount, setPermissionCount] = useState(0);
  const [permissionError, setPermissionError] = useState("");
  const [permissionRecords, setPermissionRecords] = useState<
    PermissionRecord[]
  >([]);

  // On-Duty states
  const [onDutyData, setOnDutyData] = useState<OnDutyRequest>({
    date_apply: today,
    no_of_days: "Full Day",
    start_date: null,
    end_date: null,
    session_type: null,
    reason: "",
    notify: "",
  });
  const [onDutyRecords, setOnDutyRecords] = useState<OnDutyRecord[]>([]);
  const [onDutyType, setOnDutyType] = useState("");

  // WFH states
  const [wfhData, setWfhData] = useState<WorkFromHomeRequest>({
    date_apply: "string | null",
    no_of_days: "Full Day",
    start_date: "string | null",
    end_date: "string | null",
    session_type: null,
    reason: "",
    notify: "",
  });
  const [wfhRecords, setWfhRecords] = useState<WorkFromHomeRecord[]>([]);
  const [wfhType, setWfhType] = useState("");

  // Leave states
  const [leaveData, setLeaveData] = useState<ApplyLeaveRecord[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeRecord[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalanceRecord[]>([]);

  const [leaveFormData, setLeaveFormData] = useState<ApplyLeaveCreate>({
    leave_type_id: 0,
    start_date: "",
    end_date: null,
    duration_days: 1,
    reason: "",
    notify: "",
  });
  const [leaveType, setLeaveType] = useState("");
  const [viewType, setViewType] = useState("Leave");

  const { employees, isLoading: loadingEmployees } = useGetEmployees();

  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};
    employees.forEach((emp) => {
      map[emp.employee_id] = {
        name: `${emp.firstname} ${emp.lastname}`,
        department: emp.department_name || "Unknown Department",
        role: emp.role_name || "Unknown Role",
      };
    });
    return map;
  }, [employees]);

  useEffect(() => {
    const loadImages = async () => {
      // Collect unique employee IDs from all table data
      const allIds = new Set<string>();
      leaveData.forEach((record) => allIds.add(record.approver_id || ""));
      permissionRecords.forEach((record) =>
        allIds.add(record.approved_by || "")
      );
      onDutyRecords.forEach((record) => allIds.add(record.approved_by || ""));
      wfhRecords.forEach((record) => allIds.add(record.approved_by || ""));

      if (empId && empToken) {
        allIds.add(empId); // Include current user ID
      }

      const idsToFetch = Array.from(allIds).filter(
        (id) => id && !empImages[id]
      );

      for (const id of idsToFetch) {
        if (id && empToken) {
          const imgUrl = await fetchEmployeeImage(id, empToken);
          setEmpImages((prev) => ({ ...prev, [id]: imgUrl }));
        }
      }
    };
    loadImages();
  }, [
    empId,
    empToken,
    leaveData,
    permissionRecords,
    onDutyRecords,
    wfhRecords,
  ]);

  // Create a map for leave types
  const leaveTypeMap = useMemo(() => {
    const map: Record<number, string> = {};
    leaveTypes.forEach((type) => {
      map[type.leave_type_id] = type.leave_type_name || "Unknown Leave Type";
    });
    return map;
  }, [leaveTypes]);

  const leaveTypeOptions = useMemo(() => {
    return leaveTypes.map((type) => {
      const balance = leaveBalances.find(
        (bal) => bal.leave_type_id === type.leave_type_id
      );
      const remainingDays = balance
        ? balance.balance_days - balance.used_days
        : 0;

      return {
        value: type.leave_type_id,
        label: type.leave_type_name,
        remaining: remainingDays,
      };
    });
  }, [leaveTypes, leaveBalances]);

  // Dropdown menu items
  const menuItems = [
    { label: "Leave", key: "Leave" },
    { label: "Permission", key: "Permission" },
    { label: "On-Duty", key: "On-Duty" },
    { label: "WFH", key: "WFH" },
  ];
  // Refresh function to fetch all data
  const refreshData = async () => {
    try {
      // Fetch leave records
      const leaveRecords = await getSelfApplyLeave(empToken);
      setLeaveData(leaveRecords);

      // Fetch permission records
      const permissionRes = await getSelfPermissions(empToken);
      setPermissionRecords(permissionRes);
      const thisMonth = new Date().getMonth();
      const permissionCount = permissionRes.filter((perm) => {
        const date = new Date(perm.date_apply);
        return date.getMonth() === thisMonth;
      }).length;
      setPermissionCount(permissionCount);

      // Fetch on-duty records
      const onDutyRecords = await getSelfOnDuties(empToken);
      setOnDutyRecords(onDutyRecords);

      // Fetch WFH records
      const wfhRecords = await getSelfWorkFromHome(empToken);
      setWfhRecords(wfhRecords);
    } catch (error) {
      console.error("Failed to refresh data", error);
    }
  };

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
  ];

  // Dropdown click handler
  const handleMenuClick = ({ key }: { key: string }) => {
    setViewType(key);
  };

  const formatTime = (time: string) => {
    return dayjs(`2020-01-01T${time}`).format("hh:mm A");
  };

  // Fetch leave types and balances
  useEffect(() => {
    const fetchLeaveTypesAndBalances = async () => {
      try {
        const [types, balances] = await Promise.all([
          getAllLeaveTypes(empToken),
          getLeaveBalanceByEmployee(currentEmployeeId, empToken), // Use currentEmployeeId
        ]);
        setLeaveTypes(types);
        setLeaveBalances(balances);
      } catch (error) {
        console.error("Failed to fetch leave types and balances", error);
      }
    };

    if (empToken && currentEmployeeId) {
      fetchLeaveTypesAndBalances();
    }
  }, [empToken, currentEmployeeId]);

  // Fetch leave records
  const fetchLeaveRecords = async () => {
    try {
      const records = await getSelfApplyLeave(empToken);
      setLeaveData(records);
    } catch (error) {
      console.error("Failed to fetch leave records", error);
    }
  };

  useEffect(() => {
    if (empToken) {
      fetchLeaveRecords();
    }
  }, [empToken]);

  // Permission duration calculation
  useEffect(() => {
    if (permissionData.start_time && permissionData.end_time) {
      const start = new Date(`2000-01-01T${permissionData.start_time}`);
      const end = new Date(`2000-01-01T${permissionData.end_time}`);
      const diff = (end.getTime() - start.getTime()) / (1000 * 60);
      const minutes = diff > 0 ? diff : 0;
      setPermissionData((prev) => ({
        ...prev,
        duration_minutes: minutes,
      }));
      if (minutes > 30) {
        setPermissionError("Permission cannot exceed 30 minutes.");
      } else {
        setPermissionError("");
      }
    }
  }, [permissionData.start_time, permissionData.end_time]);

  // Fetch permission records
  useEffect(() => {
    const fetchPermissionCount = async () => {
      try {
        const res = await getSelfPermissions(empToken);
        setPermissionRecords(res);
        const thisMonth = new Date().getMonth();
        const count = res.filter((perm) => {
          const date = new Date(perm.date_apply);
          return date.getMonth() === thisMonth;
        }).length;
        setPermissionCount(count);
      } catch (err) {
        console.error("Failed to check permission quota.");
      }
    };
    if (empToken) {
      fetchPermissionCount();
    }
  }, [empToken]);

  // Fetch on-duty records
  const fetchOnDutyRecords = async () => {
    try {
      const records = await getSelfOnDuties(empToken);
      setOnDutyRecords(records);
    } catch (error) {
      console.error("Failed to fetch On-Duty records", error);
    }
  };

  useEffect(() => {
    if (empToken) {
      fetchOnDutyRecords();
    }
  }, [empToken]);

  // Fetch WFH records
  const fetchWfhRecords = async () => {
    try {
      const records = await getSelfWorkFromHome(empToken);
      setWfhRecords(records);
    } catch (err) {
      console.error("Failed to fetch WFH records", err);
    }
  };

  useEffect(() => {
    fetchWfhRecords();
  }, []);

  // Calculate duration days based on dates
  useEffect(() => {
    if (leaveFormData.start_date) {
      if (leaveType === "custom" && leaveFormData.end_date) {
        const start = new Date(leaveFormData.start_date);
        const end = new Date(leaveFormData.end_date);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setLeaveFormData((prev) => ({ ...prev, duration_days: diffDays }));
      } else if (leaveType === "half") {
        setLeaveFormData((prev) => ({ ...prev, duration_days: 0.5 }));
      } else if (leaveType === "full") {
        setLeaveFormData((prev) => ({ ...prev, duration_days: 1 }));
      }
    }
  }, [leaveFormData.start_date, leaveFormData.end_date, leaveType]);

  // Handle Edit
  const handleEdit = (record: any) => {
    setSelectedRecord(record);
    setModalMode("edit");

    switch (viewType) {
      case "Leave":
        setLeaveFormData({
          leave_type_id: record.leave_type_id,
          start_date: record.start_date,
          end_date: record.end_date,
          duration_days: record.duration_days,
          reason: record.reason,
          notify: record.notify || "",
        });
        setLeaveType(
          record.duration_days === 0.5
            ? "half"
            : record.duration_days === 1
            ? "full"
            : "custom"
        );
        break;

      case "Permission":
        setPermissionData({
          date_apply: record.date_apply,
          start_time: record.start_time,
          end_time: record.end_time,
          duration_minutes: record.duration_hours * 60,
          reason: record.reason,
          notify: record.notify || "",
        });
        break;

      case "On-Duty":
        setOnDutyData({
          date_apply: record.date_apply,
          no_of_days: record.no_of_days,
          start_date: record.start_date,
          end_date: record.end_date,
          session_type: record.session_type,
          reason: record.reason,
          notify: record.notify || "",
        });
        setOnDutyType(
          record.no_of_days === "Full Day"
            ? "full"
            : record.no_of_days === "Half Day"
            ? "half"
            : "custom"
        );
        break;

      case "WFH":
        setWfhData({
          date_apply: record.date_apply,
          no_of_days: record.no_of_days,
          start_date: record.start_date,
          end_date: record.end_date,
          session_type: record.session_type,
          reason: record.reason,
          notify: record.notify || "",
        });
        setWfhType(
          record.no_of_days === "Full Day"
            ? "full"
            : record.no_of_days === "Half Day"
            ? "half"
            : "custom"
        );
        break;

      default:
        break;
    }

    // Open modal safely
    const modalEl = document.getElementById("add_leaves");
    if (modalEl) {
      const modalInstance = Modal.getInstance(modalEl) || new Modal(modalEl);
      modalInstance.show();
    }
  };

  // Unified submit handler for add and edit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (permissionError) {
      toast.error(permissionError);
      return;
    }

    try {
      if (modalMode === "add") {
        // Add mode validations and submissions
        switch (viewType) {
          case "Leave":
            await postApplyLeave(leaveFormData, empToken);
            break;

          case "Permission":
            if (permissionCount >= 3) {
              setPermissionError(
                "You can only apply for 3 permissions per month."
              );
              return;
            }
            if (permissionData.duration_minutes > 30) {
              setPermissionError("Permission cannot exceed 30 minutes.");
              return;
            }
            await postPermission(
              {
                duration_hours: Number(
                  (permissionData.duration_minutes / 60).toFixed(2)
                ),
                start_time: permissionData.start_time,
                end_time: permissionData.end_time,
                date_apply: permissionData.date_apply,
                reason: permissionData.reason,
              },
              empToken
            );
            break;

          case "On-Duty":
            const today = new Date().toISOString().split("T")[0];
            if (onDutyType === "half" && !onDutyData.session_type) {
              toast.error("Please select session type for Half Day");
              return;
            }
            if (
              onDutyType === "custom" &&
              (!onDutyData.start_date || !onDutyData.end_date)
            ) {
              toast.error("Please select start and end date for Custom days");
              return;
            }
            if (!onDutyData.notify) {
              toast.error("Please select notify person");
              return;
            }
            const onDutyPayload: OnDutyRequest = {
              date_apply: onDutyData.date_apply || today,
              no_of_days:
                onDutyType === "full"
                  ? "Full Day"
                  : onDutyType === "half"
                  ? "Half Day"
                  : "Custom",
              session_type:
                onDutyType === "half" ? onDutyData.session_type! : undefined,
              start_date:
                onDutyType === "custom"
                  ? onDutyData.start_date
                  : onDutyType === "half"
                  ? onDutyData.date_apply || today
                  : undefined,
              end_date:
                onDutyType === "custom"
                  ? onDutyData.end_date
                  : onDutyType === "half"
                  ? onDutyData.date_apply || today
                  : undefined,
              reason: onDutyData.reason,
              notify: onDutyData.notify,
            };
            await postOnDuty(onDutyPayload, empToken);
            break;

          case "WFH":
            let wfhPayload: WorkFromHomeRequest;
            if (wfhType === "full") {
              wfhPayload = {
                ...wfhData,
                no_of_days: "Full Day",
                date_apply: wfhData.date_apply,
                session_type: null,
                start_date: null,
                end_date: null,
              };
            } else if (wfhType === "half") {
              wfhPayload = {
                ...wfhData,
                no_of_days: "Half Day",
                session_type: wfhData.session_type,
                date_apply: wfhData.date_apply,
                start_date: null,
                end_date: null,
              };
            } else {
              wfhPayload = {
                ...wfhData,
                no_of_days: "Custom",
                start_date: wfhData.start_date,
                end_date: wfhData.end_date,
                date_apply: null,
                session_type: null,
              };
            }
            await postWorkFromHome(wfhPayload, empToken);
            break;
        }
      } else {
        // Edit mode
        switch (viewType) {
          case "Leave":
            await putUpdateLeave(
              selectedRecord.request_id,
              leaveFormData,
              empToken
            );
            break;
          // case "Permission":
          //   await putUpdatePermission(selectedRecord.permission_id, {
          //     duration_hours: Number(
          //       (permissionData.duration_minutes / 60).toFixed(2)
          //     ),
          //     start_time: permissionData.start_time,
          //     end_time: permissionData.end_time,
          //     date_apply: permissionData.date_apply,
          //     reason: permissionData.reason,
          //     notify: permissionData.notify,
          //   }, empToken);
          //   break;
          // case "On-Duty":
          //   const onDutyUpdatePayload: OnDutyRequest = {
          //     date_apply: onDutyData.date_apply || today,
          //     no_of_days:
          //       onDutyType === "full"
          //         ? "Full Day"
          //         : onDutyType === "half"
          //         ? "Half Day"
          //         : "Custom",
          //     session_type:
          //       onDutyType === "half" ? onDutyData.session_type! : undefined,
          //     start_date:
          //       onDutyType === "custom"
          //         ? onDutyData.start_date
          //         : onDutyType === "half"
          //         ? onDutyData.date_apply || today
          //         : undefined,
          //     end_date:
          //       onDutyType === "custom"
          //         ? onDutyData.end_date
          //         : onDutyType === "half"
          //         ? onDutyData.date_apply || today
          //         : undefined,
          //     reason: onDutyData.reason,
          //     notify: onDutyData.notify,
          //   };
          //   await putUpdateOnDuty(selectedRecord.duty_id, onDutyUpdatePayload, empToken);
          //   break;
          // case "WFH":
          //   let wfhUpdatePayload: WorkFromHomeRequest;
          //   if (wfhType === "full") {
          //     wfhUpdatePayload = {
          //       ...wfhData,
          //       no_of_days: "Full Day",
          //       date_apply: wfhData.date_apply,
          //       session_type: null,
          //       start_date: null,
          //       end_date: null,
          //     };
          //   } else if (wfhType === "half") {
          //     wfhUpdatePayload = {
          //       ...wfhData,
          //       no_of_days: "Half Day",
          //       session_type: wfhData.session_type,
          //       date_apply: wfhData.date_apply,
          //       start_date: null,
          //       end_date: null,
          //     };
          //   } else {
          //     wfhUpdatePayload = {
          //       ...wfhData,
          //       no_of_days: "Custom",
          //       start_date: wfhData.start_date,
          //       end_date: wfhData.end_date,
          //       date_apply: null,
          //       session_type: null,
          //     };
          //   }
          //   await putUpdateWorkFromHome(selectedRecord.wfh_id, wfhUpdatePayload, empToken);
          //   break;
        }
      }

      // Reset form states
      setLeaveFormData({
        leave_type_id: 0,
        start_date: "",
        end_date: null,
        duration_days: 1,
        reason: "",
        notify: "",
      });
      setLeaveType("");
      setPermissionData({
        date_apply: "",
        start_time: "",
        end_time: "",
        duration_minutes: 0,
        reason: "",
        notify: "",
      });
      setPermissionError("");
      setOnDutyData({
        date_apply: today,
        no_of_days: "Full Day",
        start_date: null,
        end_date: null,
        session_type: null,
        reason: "",
        notify: "",
      });
      setOnDutyType("");
      setWfhData({
        date_apply: null,
        no_of_days: "Full Day",
        start_date: null,
        end_date: null,
        session_type: null,
        reason: "",
        notify: "",
      });
      setWfhType("");
      setModalMode("add");
      setSelectedRecord(null);

      // Close modal
      const modalEl = document.getElementById(
        "add_leaves"
      ) as HTMLElement | null;
      if (modalEl) {
        const modalInstance = Modal.getInstance(modalEl);
        modalInstance?.show();
      }

      // Refresh data
      await refreshData();
    } catch (error: any) {
      console.error("Submission error:", error);
      toast.error("Failed to process request. Please try again.");
    }
  };

  // Table columns
  const leaveColumns: ColumnsType<ApplyLeaveRecord> = [
    {
      title: "S.No",
      render: (_text, _record, index) => index + 1,
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type_id",
      render: (leaveTypeId: number) => {
        const leaveTypeName = leaveTypeMap[leaveTypeId] || "Unknown Leave Type";
        return (
          <span className="badge rounded-pill bg-soft-info">
            {leaveTypeName}
          </span>
        );
      },
    },
    {
      title: "Duration",
      render: (_: any, record: ApplyLeaveRecord) => {
        const { start_date, end_date, duration_days } = record;

        if (duration_days === 0.5) {
          return (
            <>
              <div>
                <span className="badge bg-info-transparent">Half Day</span>
              </div>
              <div>
                <strong>Date:</strong> {start_date}
              </div>
            </>
          );
        } else if (duration_days === 1) {
          return (
            <>
              <div>
                <span className="badge bg-transparent-purple">Full Day</span>
              </div>
              <div>
                <strong>Date:</strong> {start_date}
              </div>
            </>
          );
        } else {
          return (
            <>
              <div>
                <span className="badge bg-pink-transparent">
                  {duration_days} Days
                </span>
              </div>
              <div>
                <strong>From:</strong> {start_date}
              </div>
              <div>
                <strong>To:</strong> {end_date}
              </div>
            </>
          );
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        const statusMeta: Record<
          string,
          { className: string; icon: JSX.Element }
        > = {
          Pending: {
            className: "badge rounded-pill bg-outline-warning",
            icon: (
              <AlertOctagon
                className="me-1"
                style={{ height: 12, width: 12 }}
              />
            ),
          },
          Approved: {
            className: "badge rounded-pill bg-outline-success",
            icon: <Check className="me-1" style={{ height: 12, width: 12 }} />,
          },
          Rejected: {
            className: "badge rounded-pill bg-outline-danger",
            icon: <X className="me-1" style={{ height: 12, width: 12 }} />,
          },
        };

        const meta = statusMeta[status] || {
          className: "bg-light",
          icon: null,
        };

        return (
          <span
            className={`badge d-inline-flex align-items-center ${meta.className}`}
          >
            {meta.icon}
            {status}
          </span>
        );
      },
    },
    {
      title: "Approved By",
      key: "approver_id",
      render: (_: any, record: ApplyLeaveRecord) => {
        const approver = employeeNameMap[record.approver_id || ""];
        const imageUrl =
          empImages[record.approver_id || ""] ||
          "/assets/img/defaultavatar.png";

        if (!record.approver_id) {
          return <span className="text-muted">-</span>;
        }
        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                key={imageUrl}
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt="Employee Avatar"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/assets/img/defaultavatar.png";
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{approver?.name || record.approver_id}</b>
              </p>
              <span className="fs-12">
                <strong>{approver?.role || "Unknown Role"}</strong>
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => {
        return dayjs(date).format("DD MMM YYYY");
      },
    },
    {
      title: "Status Date",
      dataIndex: "approved_date",
      render: (_: any, record: ApplyLeaveRecord) => {
        // Only show approved date if status is Approved
        if (record.status === "Approved" && record.approved_date) {
          return dayjs(record.approved_date).format("DD MMM YYYY");
        }
        return <span className="text-muted">-</span>;
      },
    },

    {
      title: "Reason",
      key: "reason",
      width: 250,
      render: (_: any, record: ApplyLeaveRecord) => {
        const { status, reject_reason, reason: employeeReason } = record;

        // Decide which reason to display
        const finalReason =
          status === "Rejected"
            ? reject_reason || employeeReason
            : employeeReason;

        return finalReason ? (
          <Input.TextArea
            value={finalReason}
            readOnly
            style={{
              height: "50px",
              overflowY: "auto",
              resize: "none",
            }}
          />
        ) : (
          <span style={{ color: "#ccc", fontSize: "12px" }}>–</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => {
        const isPending = record.status === "Pending";

        if (!isPending) {
          // Render a disabled button (no modal attached)
          return (
            <span className="btn btn-sm btn-icon disabled">
              <i className="ti ti-edit" />
            </span>
          );
        }

        return (
          <Link
            to="#"
            className="btn btn-sm btn-icon ms-auto"
            data-bs-toggle="modal"
            data-bs-target="#add_leaves"
            onClick={(e) => {
              e.preventDefault();
              handleEdit(record);
            }}
          >
            <i className="ti ti-edit" />
          </Link>
        );
      },
    },
  ];

  const permissionColumns: ColumnsType<PermissionRecord> = [
    {
      title: "S.No",
      render: (_text, _record, index) => index + 1,
    },
    {
      title: "Applied Date",
      dataIndex: "date_apply",
    },
    {
      title: "Start / End Time",
      render: (record: PermissionRecord) => (
        <>
          <span className="badge bg-transparent-purple">
            {formatTime(record.start_time)}
          </span>
          <br />
          <span className="badge bg-pink-transparent">
            {formatTime(record.end_time)}
          </span>
        </>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration_hours",
      render: (value: number) => {
        const mins = Math.round(value * 60);
        return `${mins} mins`;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        const statusMeta: Record<
          string,
          { className: string; icon: JSX.Element }
        > = {
          Pending: {
            className: "badge rounded-pill bg-outline-warning",
            icon: (
              <AlertOctagon
                className="me-1"
                style={{ height: 12, width: 12 }}
              />
            ),
          },
          Approved: {
            className: "badge rounded-pill bg-outline-success",
            icon: <Check className="me-1" style={{ height: 12, width: 12 }} />,
          },
          Rejected: {
            className: "badge rounded-pill bg-outline-danger",
            icon: <X className="me-1" style={{ height: 12, width: 12 }} />,
          },
        };

        const meta = statusMeta[status] || {
          className: "bg-light",
          icon: null,
        };

        return (
          <span
            className={`badge d-inline-flex align-items-center ${meta.className}`}
          >
            {meta.icon}
            {status}
          </span>
        );
      },
    },
    {
      title: "Approved By",
      key: "approved_by",
      render: (_: any, record: PermissionRecord) => {
        const approver = employeeNameMap[record.approved_by || ""];
        const imageUrl =
          empImages[record.approved_by || ""] ||
          "/assets/img/defaultavatar.png";

        if (!record.approved_by) {
          return <span className="text-muted">-</span>;
        }

        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                key={imageUrl}
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt="Employee Avatar"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/assets/img/defaultavatar.png";
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{approver?.name || record.approved_by}</b>
              </p>
              <span className="fs-12">
                <strong>{approver?.role || "Unknown Role"}</strong>
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => {
        return dayjs(date).format("DD MMM YYYY");
      },
    },
    {
      title: "Status Date",
      dataIndex: "approved_date",
      render: (_: any, record: PermissionRecord) => {
        if (
          (record.status === "Approved" || record.status === "Rejected") &&
          record.approved_date
        ) {
          return dayjs(record.approved_date).format("DD MMM YYYY");
        }
        return <span className="text-muted">-</span>;
      },
    },
    {
      title: "Reason",
      key: "reason",
      width: 250,
      render: (_: any, record: PermissionRecord) => {
        const { status, reason: employeeReason, reject_reason } = record;

        // Pick the reason to display: admin reason if rejected, otherwise employee reason
        const finalReason =
          status === "Rejected"
            ? reject_reason || employeeReason
            : employeeReason;

        return finalReason ? (
          <Input.TextArea
            value={finalReason}
            readOnly
            style={{
              height: "50px",
              overflowY: "auto",
              resize: "none",
            }}
          />
        ) : (
          <span style={{ color: "#ccc", fontSize: "12px" }}>–</span>
        );
      },
    },
  ];

  const dutyColumns = [
    {
      title: "S.No",
      render: (_text: any, _record: OnDutyRecord, index: number) => index + 1,
    },
    {
      title: "No. of Days",
      render: (_: any, record: OnDutyRecord) => {
        const { no_of_days, date_apply, session_type, start_date, end_date } =
          record;

        const badgeClassMap: Record<string, string> = {
          "Full Day": "badge bg-transparent-purple",
          "Half Day": "badge bg-info-transparent",
          Custom: "badge bg-pink-transparent",
        };

        const renderBadge = (label: string) => (
          <span className={badgeClassMap[label] || "badge bg-light"}>
            {label}
          </span>
        );

        if (no_of_days === "Full Day") {
          return (
            <>
              <div>
                <h6>
                  <b>Type:</b> {renderBadge("Full Day")}
                </h6>
              </div>
              <div>
                <strong>Applied Date:</strong> {date_apply || "N/A"}
              </div>
            </>
          );
        } else if (no_of_days === "Half Day") {
          return (
            <>
              <div>
                <h6>
                  <b>Type:</b> {renderBadge("Half Day")}
                </h6>
              </div>
              <div>
                <strong>Session:</strong> {session_type || "N/A"}
              </div>
              <div>
                <strong>Date:</strong> {date_apply || "N/A"}
              </div>
            </>
          );
        } else {
          return (
            <>
              <div>
                <h6>
                  <b>Type:</b> {renderBadge("Custom")}
                </h6>
              </div>
              <div>
                <strong>Start Date:</strong> {start_date || "N/A"}
              </div>
              <div>
                <strong>End Date:</strong> {end_date || "N/A"}
              </div>
            </>
          );
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        const statusMeta: Record<
          string,
          { className: string; icon: JSX.Element }
        > = {
          Pending: {
            className: "badge rounded-pill bg-outline-warning",
            icon: (
              <AlertOctagon
                className="me-1"
                style={{ height: 12, width: 12 }}
              />
            ),
          },
          Approved: {
            className: "badge rounded-pill bg-outline-success",
            icon: <Check className="me-1" style={{ height: 12, width: 12 }} />,
          },
          Rejected: {
            className: "badge rounded-pill bg-outline-danger",
            icon: <X className="me-1" style={{ height: 12, width: 12 }} />,
          },
        };

        const meta = statusMeta[status] || {
          className: "badge bg-secondary",
          icon: null,
        };

        return (
          <span className={meta.className}>
            {meta.icon}
            {status}
          </span>
        );
      },
    },
    {
      title: "Approved By",
      key: "approved_by",
      render: (_: any, record: OnDutyRecord) => {
        const approver = employeeNameMap[record.approved_by || ""];
        const imageUrl =
          empImages[record.approved_by || ""] ||
          "/assets/img/defaultavatar.png";

        if (!record.approved_by) {
          return <span className="text-muted">-</span>;
        }

        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                key={imageUrl}
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt="Employee Avatar"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/assets/img/defaultavatar.png";
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{approver?.name || record.approved_by}</b>
              </p>
              <span className="fs-12">
                <strong>{approver?.role || "Unknown Role"}</strong>
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => {
        return dayjs(date).format("DD MMM YYYY");
      },
    },
    {
      title: "Status Date",
      dataIndex: "approved_date",
      render: (_: any, record: OnDutyRecord) => {
        if (
          (record.status === "Approved" || record.status === "Rejected") &&
          record.approved_date
        ) {
          return dayjs(record.approved_date).format("DD MMM YYYY");
        }
        return <span className="text-muted">-</span>;
      },
    },
    {
      title: "Reason",
      key: "reason",
      width: 250,
      render: (_: any, record: OnDutyRecord) => {
        const { status, reason: employeeReason, reject_reason } = record;

        // Pick which reason to display
        const finalReason =
          status === "Rejected"
            ? reject_reason || employeeReason
            : employeeReason;

        return finalReason ? (
          <Input.TextArea
            value={finalReason}
            readOnly
            style={{
              height: "50px",
              overflowY: "auto",
              resize: "none",
            }}
          />
        ) : (
          <span style={{ color: "#ccc", fontSize: "12px" }}>–</span>
        );
      },
    },
  ];

  const wfhColumns = [
    {
      title: "S.No",
      render: (_text: any, _record: WorkFromHomeRecord, index: number) =>
        index + 1,
    },
    {
      title: "No. of Days",
      render: (_: any, record: WorkFromHomeRecord) => {
        const { no_of_days, date_apply, session_type, start_date, end_date } =
          record;

        const badgeClassMap: Record<string, string> = {
          "Full Day": "badge bg-transparent-purple",
          "Half Day": "badge bg-info-transparent",
          Custom: "badge bg-pink-transparent",
        };

        const renderBadge = (label: string) => (
          <span className={badgeClassMap[label] || "badge bg-light"}>
            {label}
          </span>
        );

        if (no_of_days === "Full Day") {
          return (
            <>
              <h6>
                <b>Type:</b> {renderBadge("Full Day")}
              </h6>
              <div>
                <strong>Applied Date:</strong> {date_apply || "N/A"}
              </div>
            </>
          );
        } else if (no_of_days === "Half Day") {
          return (
            <>
              <h6>
                <b>Type:</b> {renderBadge("Half Day")}
              </h6>
              <div>
                <strong>Session:</strong> {session_type || "N/A"}
              </div>
              <div>
                <strong>Date:</strong> {date_apply || "N/A"}
              </div>
            </>
          );
        } else {
          return (
            <>
              <h6>
                <b>Type:</b> {renderBadge("Custom")}
              </h6>
              <div>
                <strong>Start Date:</strong> {start_date || "N/A"}
              </div>
              <div>
                <strong>End Date:</strong> {end_date || "N/A"}
              </div>
            </>
          );
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        const statusMeta: Record<
          string,
          { className: string; icon: JSX.Element | null }
        > = {
          Pending: {
            className: "badge rounded-pill bg-outline-warning",
            icon: (
              <AlertOctagon
                className="me-1"
                style={{ height: 12, width: 12 }}
              />
            ),
          },
          Approved: {
            className: "badge rounded-pill bg-outline-success",
            icon: <Check className="me-1" style={{ height: 12, width: 12 }} />,
          },
          Rejected: {
            className: "badge rounded-pill bg-outline-danger",
            icon: <X className="me-1" style={{ height: 12, width: 12 }} />,
          },
        };

        const meta = statusMeta[status] || {
          className: "badge bg-secondary",
          icon: null,
        };
        return (
          <span className={meta.className}>
            {meta.icon}
            {status}
          </span>
        );
      },
    },
    {
      title: "Approved By",
      key: "approved_by",
      render: (_: any, record: WorkFromHomeRecord) => {
        const approver = employeeNameMap[record.approved_by || ""];
        const imageUrl =
          empImages[record.approved_by || ""] ||
          "/assets/img/defaultavatar.png";

        if (!record.approved_by) {
          return <span className="text-muted">-</span>;
        }

        return (
          <div className="d-flex align-items-center">
            <div className="avatar avatar-md">
              <img
                key={imageUrl}
                src={imageUrl}
                className="img-fluid rounded-circle"
                alt="Employee Avatar"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/assets/img/defaultavatar.png";
                }}
              />
            </div>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <b>{approver?.name || record.approved_by}</b>
              </p>
              <span className="fs-12">
                <strong>{approver?.role || "Unknown Role"}</strong>
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => {
        return dayjs(date).format("DD MMM YYYY");
      },
    },
    {
      title: "Status Date",
      key: "status_date",
      render: (_: any, record: WorkFromHomeRecord) => {
        if (record.status === "Approved" && record.approved_date) {
          return dayjs(record.approved_date).format("DD MMM YYYY");
        }
        if (record.status === "Rejected" && record.approved_date) {
          return dayjs(record.approved_date).format("DD MMM YYYY");
        }
        return <span className="text-muted">-</span>;
      },
    },
    {
      title: "Reason",
      key: "reason",
      width: 250,
      render: (_: any, record: WorkFromHomeRecord) => {
        const { status, reason: employeeReason, reject_reason } = record;

        // Choose which reason to show
        const finalReason =
          status === "Rejected"
            ? reject_reason || employeeReason
            : employeeReason;

        return finalReason ? (
          <Input.TextArea
            value={finalReason}
            readOnly
            style={{
              height: "50px",
              overflowY: "auto",
              resize: "none",
            }}
          />
        ) : (
          <span style={{ color: "#ccc", fontSize: "12px" }}>–</span>
        );
      },
    },
  ];

  const getColumns = () => {
    switch (viewType) {
      case "Permission":
        return permissionColumns;
      case "On-Duty":
        return dutyColumns;
      case "WFH":
        return wfhColumns;
      default:
        return leaveColumns;
    }
  };

  const getTableData = () => {
    let data: any[] = [];
    switch (viewType) {
      case "Permission":
        data = permissionRecords;
        break;
      case "On-Duty":
        data = onDutyRecords;
        break;
      case "WFH":
        data = wfhRecords;
        break;
      default:
        data = leaveData;
    }

    let searchableFields: string[] = [];
    switch (viewType) {
      case "Leave":
        searchableFields = ["leave_type", "reason", "status", "approver"];
        break;
      default:
        searchableFields = ["reason", "status", "approver"];
    }

    if (statusFilter) {
      data = data.filter((record) => record.status === statusFilter);
    }

    if (searchText && searchText.trim() !== "") {
      const lowerSearch = searchText.toLowerCase();
      data = data.filter((record) => {
        return searchableFields.some((field) => {
          let value: string | undefined;
          if (field === "leave_type" && viewType === "Leave") {
            value = leaveTypeMap[record.leave_type_id] || "";
          } else if (field === "approver") {
            const approverId = record.approver_id || record.approved_by || "";
            value = employeeNameMap[approverId]?.name || "";
          } else {
            value = record[field] || "";
          }
          return value ? value.toLowerCase().includes(lowerSearch) : false;
        });
      });
    }

    return data;
  };

  const getRowKey = (record: any) => {
    switch (viewType) {
      case "Permission":
        return record.permission_id;
      case "On-Duty":
        return record.duty_id;
      case "WFH":
        return record.wfh_id;
      default:
        return record.request_id;
    }
  };

  type StatusDropdownProps = {
    value: string;
    onChange: (value: string) => void;
  };

  const StatusDropdown: React.FC<StatusDropdownProps> = ({
    value,
    onChange,
  }) => {
    const selectedLabel =
      statusOptions.find((option) => option.value === value)?.label ||
      "Filter Status";
    const menu = (
      <Menu onClick={({ key }) => onChange(key as string)}>
        {statusOptions.map((option) => (
          <Menu.Item key={option.value}>{option.label}</Menu.Item>
        ))}
      </Menu>
    );
    return (
      <Dropdown overlay={menu}>
        <Button>
          {selectedLabel} <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Leaves</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Leaves
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Dropdown Button */}
              <Dropdown
                menu={{ items: menuItems, onClick: handleMenuClick }}
                placement="bottomLeft"
                trigger={["click"]}
              >
                <Button
                  className="btn btn-primary d-flex align-items-center"
                  type="default"
                >
                  Table View <DownOutlined className="ms-2" />
                </Button>
              </Dropdown>

              {/* Apply Leave Button */}
              <Button
                className="btn btn-primary d-flex align-items-center"
                data-bs-toggle="modal"
                data-bs-target="#add_leaves"
              >
                <i className="ti ti-circle-plus me-2" />
                Apply Leave
              </Button>

              {/* Collapse Icon */}
              <div className="ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Leave Widgets Section - Scrollable */}
          <div className="card mb-4 position-relative">
            <div className="card-body p-3">
              {/* Scrollable widget list */}
              <div
                className="d-flex overflow-auto"
                style={{
                  gap: "1rem",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: "0.5rem",
                }}
              >
                {leaveTypes.map((type) => {
                  const balance = leaveBalances.find(
                    (bal) => bal.leave_type_id === type.leave_type_id
                  );

                  // Safe defaults
                  const accrued = balance?.accrued_days ?? 0; // total credited
                  const used = balance?.used_days ?? 0; // leave taken
                  const remainingBalance = balance?.balance_days ?? 0; // remaining balance
                  const monthApplied = accrued; // total credited

                  // leave_type_name can be null → fallback to empty string
                  const leaveName = type.leave_type_name ?? "Unknown Leave";

                  // Icon mapping
                  const iconMap: Record<string, string> = {
                    "Sick Leave": "ti ti-heartbeat",
                    "Casual Leave": "ti ti-umbrella",
                    "Paternity Leave": "ti ti-mood-kid",
                    "Maternity Leave": "ti ti-baby-carriage",
                  };

                  const leaveIcon =
                    iconMap[leaveName] || "ti ti-calendar-event";

                  return (
                    <div
                      key={type.leave_type_id}
                      className="card flex-shrink-0"
                      style={{ minWidth: "260px", maxWidth: "260px" }}
                    >
                      <div className="card-body">
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">{leaveName}</h6>
                          <span className="avatar avatar-md d-flex bg-light rounded-circle p-2">
                            <i className={`${leaveIcon} fs-20 text-primary`} />
                          </span>
                        </div>

                        {/* Month Applied */}
                        <div className="d-flex align-items-center mb-2">
                          <span
                            className="bg-primary text-white rounded me-2"
                            style={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                            }}
                          >
                            <i className="ti ti-calendar" />
                          </span>
                          <span className="text-muted small">
                            Month Applied
                          </span>
                        </div>
                        <h3 className="mb-0">{monthApplied}</h3>

                        {/* Balance + Used */}
                        <div className="d-flex flex-wrap column-gap-3 row-gap-1 mt-2">
                          <div className="d-flex align-items-center">
                            <div
                              className="bg-success rounded-circle me-1"
                              style={{ width: "8px", height: "8px" }}
                            ></div>
                            <small className="text-muted">
                              Balance: {remainingBalance}
                            </small>
                          </div>

                          <div className="d-flex align-items-center">
                            <div
                              className="bg-danger rounded-circle me-1"
                              style={{ width: "8px", height: "8px" }}
                            ></div>
                            <small className="text-muted">Used: {used}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Leaves list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="d-flex">
                <h5 className="me-2">{viewType} List</h5>
              </div>
              <div
                className="d-flex align-items-center gap-2"
                style={{ flexWrap: "nowrap" }}
              >
                <div style={{ minWidth: "140px" }}>
                  <StatusDropdown
                    value={statusFilter}
                    onChange={setStatusFilter}
                  />
                </div>

                <Input.Search
                  placeholder="Search records..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ maxWidth: 300 }}
                  allowClear
                />
              </div>
            </div>

            <div className="card-body p-0">
              <Table
                dataSource={getTableData()}
                columns={getColumns()}
                rowKey={getRowKey}
                scroll={{ x: 1500 }}
              />
            </div>
          </div>
          {/* /Leaves list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed &amp; Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Add Leaves Modal */}
      <div className="modal fade" id="add_leaves">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Apply Form</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body pb-0">
                <div className="card rounded-4">
                  <div className="card-body">
                    {/* Tabs */}
                    <div className="card shadow-md rounded-4">
                      <ul
                        className="nav nav-tabs custom-tabs mb-4"
                        id="leaveTab"
                        role="tablist"
                      >
                        {["Leave", "Permission", "On-Duty", "WFH"].map(
                          (label, idx) => (
                            <li
                              className="nav-item"
                              role="presentation"
                              key={label}
                            >
                              <a
                                className={`nav-link ${
                                  viewType === label ? "active" : ""
                                }`}
                                id={`${label.toLowerCase()}-tab`}
                                data-bs-toggle="tab"
                                href={`#${label.toLowerCase()}-content`}
                                role="tab"
                                onClick={() => setViewType(label)}
                              >
                                {label}
                              </a>
                            </li>
                          )
                        )}
                      </ul>
                    </div>

                    {/* Tab Content */}
                    <div className="tab-content">
                      {/* Leave Tab */}
                      {viewType === "Leave" && (
                        <div className="row">
                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>Choose Leave Type </strong>
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select border-info"
                              value={leaveFormData.leave_type_id}
                              onChange={(e) =>
                                setLeaveFormData({
                                  ...leaveFormData,
                                  leave_type_id: parseInt(e.target.value),
                                })
                              }
                              required
                            >
                              <option value={0}>Select Leave Type</option>
                              {leaveTypeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>No of Days</strong>
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={leaveType}
                              onChange={(e) => setLeaveType(e.target.value)}
                              required
                            >
                              <option value="">Select</option>
                              <option value="full">Full Day</option>
                              <option value="half">Half Day</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>

                          {/* Conditional Fields for Leave */}
                          {leaveType === "full" && (
                            <div className="mb-3 col-md-6">
                              <label className="form-label">
                                <strong>Select Date</strong>
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                value={leaveFormData.start_date}
                                onChange={(e) =>
                                  setLeaveFormData({
                                    ...leaveFormData,
                                    start_date: e.target.value,
                                    end_date: null,
                                  })
                                }
                                required
                              />
                            </div>
                          )}

                          {leaveType === "half" && (
                            <>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Session</strong>
                                </label>
                                <select className="form-select">
                                  <option>Morning</option>
                                  <option>Afternoon</option>
                                </select>
                              </div>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Select Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={leaveFormData.start_date}
                                  onChange={(e) =>
                                    setLeaveFormData({
                                      ...leaveFormData,
                                      start_date: e.target.value,
                                      end_date: null,
                                    })
                                  }
                                  required
                                />
                              </div>
                            </>
                          )}

                          {leaveType === "custom" && (
                            <>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Start Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={leaveFormData.start_date}
                                  onChange={(e) =>
                                    setLeaveFormData({
                                      ...leaveFormData,
                                      start_date: e.target.value,
                                    })
                                  }
                                  required
                                />
                              </div>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>End Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={leaveFormData.end_date || ""}
                                  onChange={(e) =>
                                    setLeaveFormData({
                                      ...leaveFormData,
                                      end_date: e.target.value,
                                    })
                                  }
                                  min={leaveFormData.start_date}
                                  required
                                />
                              </div>
                            </>
                          )}

                          {leaveType && (
                            <div className="mb-3 col-md-6">
                              <label className="form-label">
                                <strong>Total Days</strong>
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                value={leaveFormData.duration_days}
                                readOnly
                                step="0.5"
                              />
                            </div>
                          )}

                          <div className="mb-3 col-md-12">
                            <label className="form-label">
                              <strong>Reason</strong>
                              <span className="text-danger">*</span>
                            </label>
                            <textarea
                              className="form-control"
                              rows={3}
                              placeholder="Enter the reason"
                              value={leaveFormData.reason}
                              onChange={(e) =>
                                setLeaveFormData({
                                  ...leaveFormData,
                                  reason: e.target.value,
                                })
                              }
                              required
                            ></textarea>
                          </div>
                          <div className="mb-3 col-md-12">
                            <label className="form-label fw-bold">
                              Report <span className="text-danger">*</span>
                            </label>
                            <Form.Select
                              value={leaveFormData.notify} // ✅ bind to form state
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) =>
                                setLeaveFormData({
                                  ...leaveFormData,
                                  notify: e.target.value,
                                })
                              }
                              required
                            >
                              <option value="">Select Report</option>
                              {employees
                                .filter(
                                  (emp) =>
                                    emp.role_name === "Admin" ||
                                    emp.role_name === "Manager"
                                )
                                .map((emp) => (
                                  <option
                                    key={emp.employee_id}
                                    value={emp.employee_id}
                                  >
                                    {`${emp.firstname} ${emp.lastname} (${emp.role_name})`}
                                  </option>
                                ))}
                            </Form.Select>
                          </div>
                        </div>
                      )}

                      {/* Permission Tab */}
                      {viewType === "Permission" && (
                        <div className="row">
                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>Select Date</strong>
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={permissionData.date_apply}
                              onChange={(e) =>
                                setPermissionData({
                                  ...permissionData,
                                  date_apply: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>Start Time</strong>
                            </label>
                            <TimePicker
                              use12Hours
                              format="h:mm A"
                              className="form-control w-100"
                              value={
                                permissionData.start_time
                                  ? dayjs(permissionData.start_time, "HH:mm")
                                  : null
                              }
                              onChange={(time) =>
                                setPermissionData({
                                  ...permissionData,
                                  start_time: time ? time.format("HH:mm") : "",
                                })
                              }
                            />
                          </div>

                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>End Time</strong>
                            </label>
                            <TimePicker
                              use12Hours
                              format="h:mm A"
                              className="form-control w-100"
                              value={
                                permissionData.end_time
                                  ? dayjs(permissionData.end_time, "HH:mm")
                                  : null
                              }
                              onChange={(time) =>
                                setPermissionData({
                                  ...permissionData,
                                  end_time: time ? time.format("HH:mm") : "",
                                })
                              }
                            />
                          </div>
                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>Total Mins</strong>
                            </label>
                            <input
                              type="number"
                              className={`form-control ${
                                permissionData.duration_minutes > 30
                                  ? "is-invalid"
                                  : ""
                              }`}
                              value={permissionData.duration_minutes}
                              readOnly
                            />
                            {permissionError && (
                              <div className="invalid-feedback d-block">
                                {permissionError}
                              </div>
                            )}
                          </div>
                          <div className="mb-3 col-md-12">
                            <label className="form-label">
                              <strong>Reason</strong>
                            </label>
                            <textarea
                              className="form-control"
                              rows={2}
                              value={permissionData.reason}
                              onChange={(e) =>
                                setPermissionData({
                                  ...permissionData,
                                  reason: e.target.value,
                                })
                              }
                            ></textarea>
                          </div>
                          <div className="mb-3 col-md-12">
                            <label className="form-label fw-bold">
                              Report <span className="text-danger">*</span>
                            </label>
                            <Form.Select
                              value={permissionData.notify}
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) =>
                                setPermissionData({
                                  ...permissionData,
                                  notify: e.target.value,
                                })
                              }
                              required
                            >
                              <option value="">Select Report</option>
                              {employees
                                .filter(
                                  (emp) =>
                                    emp.role_name === "Admin" ||
                                    emp.role_name === "Manager"
                                )
                                .map((emp) => (
                                  <option
                                    key={emp.employee_id}
                                    value={emp.employee_id}
                                  >
                                    {`${emp.firstname} ${emp.lastname} (${emp.role_name})`}
                                  </option>
                                ))}
                            </Form.Select>
                          </div>
                        </div>
                      )}

                      {/* On-Duty Tab */}
                      {viewType === "On-Duty" && (
                        <div className="row">
                          {/* Dropdown to select On-Duty type */}
                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>No of Days</strong>
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={onDutyType}
                              onChange={(e) => setOnDutyType(e.target.value)}
                            >
                              <option value="">Select</option>
                              <option value="full">Full Day</option>
                              <option value="half">Half Day</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>

                          {/* Full Day Fields */}
                          {onDutyType === "full" && (
                            <div className="mb-3 col-md-6">
                              <label className="form-label">
                                <strong>Select Date</strong>
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                value={onDutyData.date_apply || ""}
                                onChange={(e) =>
                                  setOnDutyData({
                                    ...onDutyData,
                                    date_apply: e.target.value,
                                  })
                                }
                              />
                            </div>
                          )}

                          {/* Half Day Fields */}
                          {onDutyType === "half" && (
                            <>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Session</strong>
                                </label>
                                <select
                                  className="form-select"
                                  value={onDutyData.session_type ?? ""}
                                  onChange={(e) =>
                                    setOnDutyData({
                                      ...onDutyData,
                                      session_type: e.target.value as
                                        | "Morning"
                                        | "Afternoon",
                                    })
                                  }
                                >
                                  <option value="">Select</option>
                                  <option value="Morning">Morning</option>
                                  <option value="Afternoon">Afternoon</option>
                                </select>
                              </div>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Select Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={onDutyData.date_apply || ""}
                                  onChange={(e) =>
                                    setOnDutyData({
                                      ...onDutyData,
                                      date_apply: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </>
                          )}

                          {/* Custom Fields */}
                          {onDutyType === "custom" && (
                            <>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Start Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={onDutyData.start_date || ""}
                                  onChange={(e) =>
                                    setOnDutyData({
                                      ...onDutyData,
                                      start_date: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>End Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={onDutyData.end_date || ""}
                                  onChange={(e) =>
                                    setOnDutyData({
                                      ...onDutyData,
                                      end_date: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </>
                          )}

                          {/* Reason Field */}
                          <div className="mb-3 col-md-12">
                            <label className="form-label">
                              <strong>Reason</strong>
                            </label>
                            <textarea
                              className="form-control"
                              rows={2}
                              value={onDutyData.reason}
                              onChange={(e) =>
                                setOnDutyData({
                                  ...onDutyData,
                                  reason: e.target.value,
                                })
                              }
                            ></textarea>
                          </div>
                          <div className="mb-3 col-md-12">
                            <label className="form-label fw-bold">
                              Report <span className="text-danger">*</span>
                            </label>
                            <Form.Select
                              value={onDutyData.notify} // ✅ bind to form state
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) =>
                                setOnDutyData({
                                  ...onDutyData,
                                  notify: e.target.value,
                                })
                              }
                              required
                            >
                              <option value="">Select Report</option>
                              {employees
                                .filter(
                                  (emp) =>
                                    emp.role_name === "Admin" ||
                                    emp.role_name === "Manager"
                                )
                                .map((emp) => (
                                  <option
                                    key={emp.employee_id}
                                    value={emp.employee_id}
                                  >
                                    {`${emp.firstname} ${emp.lastname} (${emp.role_name})`}
                                  </option>
                                ))}
                            </Form.Select>
                          </div>
                        </div>
                      )}

                      {/* WFH Tab */}
                      {viewType === "WFH" && (
                        <div className="row">
                          {/* No of Days */}
                          <div className="mb-3 col-md-6">
                            <label className="form-label">
                              <strong>No of Days</strong>
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={wfhType}
                              onChange={(e) => setWfhType(e.target.value)}
                            >
                              <option value="">Select</option>
                              <option value="full">Full Day</option>
                              <option value="half">Half Day</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>

                          {/* Full Day */}
                          {wfhType === "full" && (
                            <div className="mb-3 col-md-6">
                              <label className="form-label">
                                <strong>Select Date</strong>
                              </label>
                              <input
                                type="date"
                                className="form-control"
                                value={wfhData.date_apply ?? ""}
                                onChange={(e) =>
                                  setWfhData({
                                    ...wfhData,
                                    date_apply: e.target.value,
                                  })
                                }
                              />
                            </div>
                          )}

                          {/* Half Day */}
                          {wfhType === "half" && (
                            <>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Session</strong>
                                </label>
                                <select
                                  className="form-select"
                                  value={wfhData.session_type ?? ""}
                                  onChange={(e) =>
                                    setWfhData({
                                      ...wfhData,
                                      session_type: e.target.value as
                                        | "Morning"
                                        | "Afternoon",
                                    })
                                  }
                                >
                                  <option value="">Select</option>
                                  <option value="Morning">Morning</option>
                                  <option value="Afternoon">Afternoon</option>
                                </select>
                              </div>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Select Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={wfhData.date_apply ?? ""}
                                  onChange={(e) =>
                                    setWfhData({
                                      ...wfhData,
                                      date_apply: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </>
                          )}

                          {/* Custom */}
                          {wfhType === "custom" && (
                            <>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>Start Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={wfhData.start_date || ""}
                                  onChange={(e) =>
                                    setWfhData({
                                      ...wfhData,
                                      start_date: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">
                                  <strong>End Date</strong>
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={wfhData.end_date || ""}
                                  onChange={(e) =>
                                    setWfhData({
                                      ...wfhData,
                                      end_date: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </>
                          )}

                          {/* Reason */}
                          <div className="mb-3 col-md-12">
                            <label className="form-label">
                              <strong>Reason</strong>
                            </label>
                            <textarea
                              className="form-control"
                              rows={2}
                              value={wfhData.reason}
                              onChange={(e) =>
                                setWfhData({
                                  ...wfhData,
                                  reason: e.target.value,
                                })
                              }
                            ></textarea>
                          </div>
                          <div className="mb-3 col-md-12">
                            <label className="form-label fw-bold">
                              Report<span className="text-danger">*</span>
                            </label>
                            <Form.Select
                              value={wfhData.notify} // ✅ bind to form state
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) =>
                                setWfhData({
                                  ...wfhData,
                                  notify: e.target.value,
                                })
                              }
                              required
                            >
                              <option value="">Select Report</option>
                              {employees
                                .filter(
                                  (emp) =>
                                    emp.role_name === "Admin" ||
                                    emp.role_name === "Manager"
                                )
                                .map((emp) => (
                                  <option
                                    key={emp.employee_id}
                                    value={emp.employee_id}
                                  >
                                    {`${emp.firstname} ${emp.lastname} (${emp.role_name})`}
                                  </option>
                                ))}
                            </Form.Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    // Reset Leave form data
                    setLeaveFormData({
                      leave_type_id: 0,
                      start_date: "",
                      end_date: null,
                      duration_days: 1,
                      reason: "",
                      notify: "",
                    });
                    setLeaveType("");

                    // Reset Permission form data
                    setPermissionData({
                      date_apply: "",
                      start_time: "",
                      end_time: "",
                      duration_minutes: 0,
                      reason: "",
                      notify: "",
                    });

                    // Reset On-Duty form data
                    setOnDutyData({
                      date_apply: "",
                      no_of_days: "Full Day",
                      start_date: null,
                      end_date: null,
                      session_type: null,
                      reason: "",
                      notify: "",
                    });
                    setOnDutyType("");

                    // Reset WFH form data
                    setWfhData({
                      date_apply: "",
                      no_of_days: "Full Day",
                      start_date: null,
                      end_date: null,
                      session_type: null,
                      reason: "",
                      notify: "",
                    });
                    setWfhType("");
                  }}
                >
                  Reset
                </button>
                <button type="submit" className="btn btn-primary text-white">
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
export default LeaveEmployee;
